import * as BABYLON from '@babylonjs/core';
import { GLTF2Export } from '@babylonjs/serializers';
import { pipe } from 'fp-ts/lib/function';
import { UAParser } from 'ua-parser-js';
import { Type } from './transform';
export const createPBRSkybox = (scene, o) => {
    if (o.environment.skybox === false) {
        return null;
    }
    const environmentTexture = BABYLON.CubeTexture.CreateFromPrefilteredData(o.environment.skybox.texture, scene);
    environmentTexture.rotationY = o.environment.skybox.rotationY ?? 2.8;
    // use the skybox for the lighting only, but rely on a clear color for the background instead
    scene.clearColor = BABYLON.Color4.FromHexString('#f8f8f8');
    scene.environmentTexture = environmentTexture;
    scene.environmentIntensity = o.environment.skybox.intensity ?? 1.0;
};
export function createLights(scene) {
    const light = new BABYLON.DirectionalLight('downlight', new BABYLON.Vector3(0, -1, 0), scene);
    light.position = new BABYLON.Vector3(0, 1.5, 0);
    light.intensity = 0;
    light.shadowEnabled = true;
    light.shadowMinZ = 0.1;
    light.shadowMaxZ = 1.5;
    light.orthoBottom = -13;
    light.orthoTop = 13;
    light.orthoLeft = -13;
    light.orthoRight = 13;
    light.autoUpdateExtends = false;
    light.autoCalcShadowZBounds = false;
    const shadowGenerator = new BABYLON.ShadowGenerator(4096, light);
    shadowGenerator.bias = -0.01;
    shadowGenerator.useBlurExponentialShadowMap = true;
    shadowGenerator.useKernelBlur = true;
    shadowGenerator.blurKernel = 24;
    shadowGenerator.depthScale = 0.1;
    shadowGenerator.setDarkness(0.2);
}
export function createSsao(scene) {
    const ssao = new BABYLON.SSAO2RenderingPipeline('ssao', scene, 1.0);
    ssao.samples = 30;
    ssao.totalStrength = 2;
    ssao.base = 0;
    ssao.maxZ = 100;
    ssao.minZAspect = 0.1;
    ssao.radius = 0.025;
    ssao.epsilon = 0.013;
    ssao.bilateralSamples = 11;
    ssao.bilateralSoften = 0;
    ssao.bilateralTolerance = 0;
    // Using SSAO on our snapshot camera seems to cause problems - namely, the snapshot fails
    // to run, which then seems to cause grief for the whole scene resulting in the scene
    // no longer working.
    // We've opted to avoid attaching SSAO to the snapshot camera instead. See issue: https://fixtuur-squad.monday.com/boards/1194100370/pulses/1235417409
    const cameras = scene.cameras.filter(cam => cam.name !== 'snapshotCamera');
    scene.postProcessRenderPipelineManager.attachCamerasToRenderPipeline('ssao', cameras);
    // Non-Safari browsers on Mac OS have major issues with framebuffer msaa, resulting
    // in the 3d viewer showing only a black screen on these platforms.
    // To avoid this, we employ user-agent sniffing and fall back to FXAA postprocessing.
    if (hasFramebufferMsaaBug()) {
        const postprocess = new BABYLON.DefaultRenderingPipeline('postprocess', false, scene, cameras);
        postprocess.fxaaEnabled = true;
    }
    else if (scene.prePassRenderer) {
        scene.prePassRenderer.samples = 4;
    }
}
function hasFramebufferMsaaBug() {
    const { browser, os } = new UAParser().getResult();
    return os.name === 'Mac OS' && browser.name !== 'Safari';
}
export const generateGltf = async (scene, download = false, filename, partToExclude) => {
    await import('@babylonjs/serializers');
    const sceneCopy = Object.assign(scene);
    const scenePBRMaterials = sceneCopy.materials.filter(material => material instanceof BABYLON.PBRMaterial);
    scenePBRMaterials.forEach(pbr => (pbr.ambientTexture = null));
    /** Return true if the provided node IS a ROOT_CONFIGURATOR_TRANSFORM or any of its parents are (recursively) */
    const shouldExportNode = (node) => {
        if (partToExclude && node.metadata?.treeNode?.attachmentTag === partToExclude)
            return false;
        return (node.name === Type.ROOT_FURNITURE_TRANSFORM ||
            (node.parent !== undefined &&
                node.parent !== null &&
                (node.parent.name === Type.ROOT_FURNITURE_TRANSFORM ||
                    shouldExportNode(node.parent))));
    };
    filename = filename ?? 'Configuration';
    const glb = await GLTF2Export.GLBAsync(sceneCopy, filename, {
        shouldExportNode,
        animationSampleRate: 0,
    });
    download && glb.downloadFiles();
    return glb.glTFFiles[`${filename}.glb`];
};
export const generateModelJson = (scene) => ({
    models: scene.transformNodes
        // grab all particle transforms
        .filter(node => node.name === Type.PARTICLE_TRANSFORM)
        // grab the id from the particle transform node and all child nodes of type PARTICLE_DELTA_ORIGIN_TRANSFORM
        // (containing our leaf node transform data)
        .flatMap(node => node
        .getChildTransformNodes(true)
        .filter(deltaOriginNode => deltaOriginNode.name === Type.PARTICLE_DELTA_ORIGIN_TRANSFORM)
        .map(deltaOriginNode => ({ particleId: node.id, transform: deltaOriginNode })))
        // transform position and orientation to world (i.e. with respect to world origin 0,0,0 and identity quaternion)
        .map(node => {
        const position = BABYLON.Vector3.Zero();
        const orientation = BABYLON.Quaternion.Identity();
        node.transform.getWorldMatrix().decompose(BABYLON.Vector3.One(), orientation, position);
        return { assetId: node.particleId, position, orientation };
    })
        // convert to ModelRequest
        .map(node => ({
        assetId: node.assetId,
        position: {
            x: node.position.x,
            y: node.position.y,
            z: node.position.z,
        },
        orientation: {
            w: node.orientation.w,
            x: node.orientation.x,
            y: node.orientation.y,
            z: node.orientation.z,
        },
    })),
});
export const generateModularModelJson = (scene) => ({
    models: scene.transformNodes
        .filter(node => node.name === Type.PARTICLE_TRANSFORM)
        .map(node => ({
        assetId: node.id,
        position: {
            x: node.position.x,
            y: node.position.y,
            z: node.position.z,
        },
        orientation: {
            w: node.rotationQuaternion?.w ?? 0,
            x: node.rotationQuaternion?.x ?? 0,
            y: node.rotationQuaternion?.y ?? 0,
            z: node.rotationQuaternion?.z ?? 0,
        },
    })),
});
export const createHighlightLayer = (scene) => {
    pipe(scene.getCameraByName('defaultCamera'), camera => new BABYLON.HighlightLayer('highlightLayer', scene, {
        camera,
        isStroke: true,
        mainTextureFixedSize: 2048,
    }), hl => (hl.innerGlow = false));
};
